/**
 * FatturazioneSection — F-002 Phase 4 (IMPL-P4-006).
 *
 * Tab "Fatturazione" del BillingProfileForm:
 *  - PF: CFInput (16 char)
 *  - PG: PIVA (input controllato — riusa CFInput in modalità PG perché in
 *        Italia la P.IVA è un CF PG di 11 cifre con stesso checksum) +
 *        sub-tabs SDI Code / PEC SDI (uno dei due obbligatorio)
 *  - Sezione indirizzo: indirizzo, cap, città, provincia, nazione
 *
 * Usa `useFormContext<BillingProfileUpsertInput>()` per leggere/scrivere
 * i campi via `Controller` (CF/PIVA sono controlled custom inputs).
 */
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { BillingProfileUpsertInput } from '@app/shared';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CFInput } from './CFInput';

type SdiSubtab = 'sdiCode' | 'sdiPec';

export function FatturazioneSection() {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<BillingProfileUpsertInput>();

  const type = watch('type');
  const sdiCode = watch('sdiCode' as never) as unknown as string | undefined;
  const sdiPec = watch('sdiPec' as never) as unknown as string | undefined;

  const [sdiSubtab, setSdiSubtab] = useState<SdiSubtab>(() => (sdiPec && !sdiCode ? 'sdiPec' : 'sdiCode'));

  return (
    <div className="space-y-6">
      {/* CF (PF) */}
      {type === 'PF' ? (
        <div className="space-y-2">
          <Label htmlFor="codiceFiscale">Codice fiscale</Label>
          <Controller
            control={control}
            name={'codiceFiscale' as const}
            render={({ field, fieldState }) => (
              <CFInput
                id="codiceFiscale"
                cfType="PF"
                value={(field.value as string | undefined) ?? ''}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        </div>
      ) : null}

      {/* PIVA (PG) */}
      {type === 'PG' ? (
        <div className="space-y-2">
          <Label htmlFor="piva">Partita IVA</Label>
          <Controller
            control={control}
            name={'piva' as const}
            render={({ field, fieldState }) => (
              <CFInput
                id="piva"
                cfType="PG"
                value={(field.value as string | undefined) ?? ''}
                onChange={field.onChange}
                error={fieldState.error?.message}
                placeholder="01234567890"
              />
            )}
          />
        </div>
      ) : null}

      {/* Indirizzo */}
      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold">Indirizzo</legend>

        <div className="space-y-2">
          <Label htmlFor="indirizzo">Indirizzo</Label>
          <Input
            id="indirizzo"
            autoComplete="street-address"
            aria-invalid={Boolean(errors.indirizzo)}
            {...register('indirizzo')}
          />
          {errors.indirizzo ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {errors.indirizzo.message as string}
            </p>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="cap">CAP</Label>
            <Input
              id="cap"
              autoComplete="postal-code"
              inputMode="numeric"
              maxLength={5}
              aria-invalid={Boolean(errors.cap)}
              {...register('cap')}
            />
            {errors.cap ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {errors.cap.message as string}
              </p>
            ) : null}
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="citta">Città</Label>
            <Input
              id="citta"
              autoComplete="address-level2"
              aria-invalid={Boolean(errors.citta)}
              {...register('citta')}
            />
            {errors.citta ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {errors.citta.message as string}
              </p>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="provincia">Provincia</Label>
            <Input
              id="provincia"
              autoComplete="address-level1"
              maxLength={2}
              className="uppercase"
              aria-invalid={Boolean(errors.provincia)}
              {...register('provincia')}
            />
            {errors.provincia ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {errors.provincia.message as string}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nazione">Nazione (ISO)</Label>
            <Input
              id="nazione"
              autoComplete="country"
              maxLength={2}
              className="uppercase"
              defaultValue="IT"
              aria-invalid={Boolean(errors.nazione)}
              {...register('nazione')}
            />
            {errors.nazione ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {errors.nazione.message as string}
              </p>
            ) : null}
          </div>
        </div>
      </fieldset>

      {/* SDI / PEC (solo PG) */}
      {type === 'PG' ? (
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold">Recapito SDI</legend>
          <p className="text-xs text-muted-foreground">
            Inserisci almeno uno tra Codice SDI o PEC SDI.
          </p>

          <div role="tablist" aria-label="Tipo recapito SDI" className="inline-flex rounded-md border p-1">
            <button
              type="button"
              role="tab"
              aria-selected={sdiSubtab === 'sdiCode'}
              onClick={() => setSdiSubtab('sdiCode')}
              className={cn(
                'rounded px-3 py-1 text-sm font-medium transition-colors',
                sdiSubtab === 'sdiCode'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent',
              )}
            >
              Codice SDI
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sdiSubtab === 'sdiPec'}
              onClick={() => setSdiSubtab('sdiPec')}
              className={cn(
                'rounded px-3 py-1 text-sm font-medium transition-colors',
                sdiSubtab === 'sdiPec'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent',
              )}
            >
              PEC SDI
            </button>
          </div>

          {sdiSubtab === 'sdiCode' ? (
            <div className="space-y-2">
              <Label htmlFor="sdiCode">Codice SDI (7 caratteri)</Label>
              <Input
                id="sdiCode"
                maxLength={7}
                className="uppercase font-mono"
                aria-invalid={Boolean('sdiCode' in errors && errors.sdiCode)}
                {...register('sdiCode' as never)}
              />
              {'sdiCode' in errors && errors.sdiCode ? (
                <p role="alert" className="text-sm font-medium text-destructive">
                  {errors.sdiCode.message as string}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="sdiPec">PEC SDI</Label>
              <Input
                id="sdiPec"
                type="email"
                autoComplete="email"
                aria-invalid={Boolean('sdiPec' in errors && errors.sdiPec)}
                {...register('sdiPec' as never)}
              />
              {'sdiPec' in errors && errors.sdiPec ? (
                <p role="alert" className="text-sm font-medium text-destructive">
                  {errors.sdiPec.message as string}
                </p>
              ) : null}
            </div>
          )}
        </fieldset>
      ) : null}
    </div>
  );
}
